(() => {
  const normalize = (value) => String(value ?? '').trim().replace(/\s+/g, '').toLocaleLowerCase();
  const hasTimezone = (value) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(String(value).trim());
  const validCalendar = value => {
    const match=String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,3})?)?)?(?:(Z)|([+-])(\d{2}):?(\d{2}))?$/i);
    if(!match) return false;
    const [,year,month,day,hour='0',minute='0',second='0',,sign,offsetHour='0',offsetMinute='0']=match;
    const daysInMonth=new Date(Date.UTC(Number(year),Number(month),0)).getUTCDate();
    return Number(month)>=1 && Number(month)<=12 && Number(day)>=1 && Number(day)<=daysInMonth && Number(hour)<=23 && Number(minute)<=59 && Number(second)<=59 && (!sign || (Number(offsetHour)<=23 && Number(offsetMinute)<=59));
  };

  function londonParts(timestamp) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
    }).formatToParts(new Date(timestamp));
    return Object.fromEntries(parts.filter((part) => part.type !== 'literal').map(({ type, value }) => [type, Number(value)]));
  }

  function parseLegacyLondon(value) {
    if(!validCalendar(value)) return NaN;
    const match = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/);
    if (!match) return NaN;
    const [, year, month, day, hour = '23', minute = '59', second = '59', fraction = '0'] = match;
    const target = {
      year: Number(year), month: Number(month), day: Number(day), hour: Number(hour),
      minute: Number(minute), second: Number(second)
    };
    const wallTime = Date.UTC(target.year, target.month - 1, target.day, target.hour, target.minute, target.second, Number(`0.${fraction}`) * 1000);
    const offsets = new Set();
    for (let delta = -36; delta <= 36; delta += 6) {
      const probe = wallTime + delta * 60 * 60 * 1000;
      const local = londonParts(probe);
      offsets.add(Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second) - Math.floor(probe / 1000) * 1000);
    }
    const candidates = [...offsets].map((offset) => wallTime - offset);
    const exact = candidates.filter((candidate) => {
      const local = londonParts(candidate);
      return Object.keys(target).every((key) => local[key] === target[key]);
    });
    if (exact.length) return Math.max(...exact);

    // A legacy wall time in the spring-forward gap has no exact UK instant.
    // Move it to the first valid local time after the requested clock time.
    const future = candidates.map((timestamp) => ({ timestamp, local: londonParts(timestamp) }))
      .filter(({ local }) => Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second) >= wallTime)
      .sort((a, b) => Date.UTC(a.local.year, a.local.month - 1, a.local.day, a.local.hour, a.local.minute, a.local.second)
        - Date.UTC(b.local.year, b.local.month - 1, b.local.day, b.local.hour, b.local.minute, b.local.second));
    return future[0]?.timestamp ?? NaN;
  }

  function parseExpiry(value, allowLegacyLondonTime) {
    const raw = String(value ?? '').trim();
    if (!raw) return null;
    if(!validCalendar(raw)) return NaN;
    if (!hasTimezone(raw)) {
      if (!allowLegacyLondonTime) return NaN;
      return parseLegacyLondon(raw);
    }
    if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) return NaN;
    return Date.parse(raw);
  }

  function parseLines(raw) {
    if (Array.isArray(raw)) raw = raw.join('\n');
    return String(raw ?? '').split(/\r?\n/).map((line) => {
      const separator = line.indexOf('|');
      const head = separator < 0 ? line : line.slice(0, separator);
      const colon = head.indexOf(':');
      const identifier = colon < 0 ? '' : head.slice(0, colon).trim();
      const code = colon < 0 ? head.trim() : head.slice(colon + 1).trim();
      const fields = separator < 0 ? [code] : [code, ...line.slice(separator + 1).split('|')];
      return {
        identifier,
        code: String(fields[0] ?? '').trim(),
        endDate: String(fields[1] ?? '').trim(),
        amount: String(fields[2] ?? '').trim(),
        text: String(fields[3] ?? '').trim()
      };
    }).filter((line) => line.code);
  }

  function selectLine(lines, variant) {
    if (!variant) return null;
    const id = String(variant.id ?? '');
    const title = normalize(variant.title);
    const exactId = lines.find((line) => line.identifier === id);
    if (exactId) return exactId;
    const exactTitle = lines.find((line) => normalize(line.identifier) === title);
    if (exactTitle) return exactTitle;
    const options = Array.isArray(variant.options) ? variant.options.map(normalize).filter(Boolean) : [];
    const optionMatches = lines.filter((line) => options.some((option) => normalize(line.identifier) === option));
    if (optionMatches.length === 1) return optionMatches[0];
    return lines.find((line) => !line.identifier) || null;
  }

  if (!customElements.get('listing-coupon-card')) {
    customElements.define('listing-coupon-card', class extends HTMLElement {
      connectedCallback() {
        if (this._connected || this._initializationScheduled) return;
        this._initializationScheduled = true;
        queueMicrotask(() => {
          this._initializationScheduled = false;
          if (!this.isConnected || this._connected) return;
          this._initialize();
        });
      }

      _initialize() {
        if (!this.isConnected || this._connected) return;
        this._connected = true;
        this._sectionId = this.dataset.sectionId || this.closest('[data-section-id]')?.dataset.sectionId || '';
        this._variant = null;
        this._timer = 0;
        this._copyButton = this.querySelector('[data-coupon-copy]');
        this._code = this.querySelector('[data-coupon-code]');
        this._offer = this.querySelector('[data-coupon-offer]');
        this._offerAmount = this.querySelector('[data-coupon-amount]');
        this._offerText = this.querySelector('[data-coupon-offer-text]');
        this._status = this.querySelector('[data-coupon-status]');
        this._timerNode = this.querySelector('[data-coupon-timer]');
        this._configNode = this.querySelector('[data-coupon-config]');
        try {
          this._config = JSON.parse(this._configNode?.textContent || '{}');
        } catch {
          this._config = {};
        }
        this._lines = parseLines(this._config.lines);
        this._revision = 0;
        this._variants = Array.isArray(this._config.variants) ? this._config.variants : [];
        this._variant = this._variants.find((variant) => String(variant.id) === String(this.dataset.variantId)) || this._variants[0] || null;
        this._onVariantChange = (event) => {
          if (event.detail?.sectionId && this._sectionId && String(event.detail.sectionId) !== String(this._sectionId)) return;
          this._variant = event.detail?.variant || null;
          this._renderCoupon();
        };
        this._onCopy = () => this._copyCode();
        document.addEventListener('product:variant-change', this._onVariantChange);
        this._copyButton?.addEventListener('click', this._onCopy);
        this._renderCoupon();
      }

      disconnectedCallback() {
        this._connected = false;
        this._initializationScheduled = false;
        document.removeEventListener('product:variant-change', this._onVariantChange);
        this._copyButton?.removeEventListener('click', this._onCopy);
        window.clearInterval(this._timer);
        window.clearTimeout(this._copyReset);
        this._timer = 0;
      }

      _renderCoupon() {
        this._revision += 1;
        window.clearInterval(this._timer);
        this._timer = 0;
        const selected = selectLine(this._lines, this._variant);
        const isStatic = this._config.source === 'block';
        if (!this._variant) {
          this.hidden = true;
          return;
        }
        const code = isStatic ? String(this._config.fallbackCode || '').trim() : selected?.code;
        if (!code) {
          this.hidden = true;
          return;
        }
        const offer = isStatic
          ? this._splitOffer(this._plainText(this._config.fallbackOffer))
          : {
              amount: selected?.amount || this._config.fallbackAmount || '',
              text: selected?.text || this._config.fallbackText || ''
            };
        const endDate = isStatic ? this._config.fallbackEndDate : (selected?.endDate || this._config.fallbackEndDate || '');
        const expiry = parseExpiry(endDate, !isStatic && endDate === selected?.endDate);
        if (endDate && (!Number.isFinite(expiry) || expiry <= Date.now())) {
          this.hidden = true;
          this._copyButton && (this._copyButton.disabled = true);
          return;
        }
        this._code.textContent = code;
        this._offerAmount.textContent = offer.amount;
        this._offerText.textContent = offer.text;
        this._copyButton.disabled = false;
        this._copyButton.textContent = this._config.copyLabel || 'Copy';
        this.hidden = false;
        if (expiry) {
          this._timerNode.hidden = false;
          this._updateTimer(expiry);
          this._timer = window.setInterval(() => this._updateTimer(expiry), 1000);
        } else {
          this._timerNode.hidden = true;
        }
      }

      _plainText(value) {
        if (!value) return '';
        const parsed = new DOMParser().parseFromString(String(value), 'text/html');
        return parsed.body.textContent.trim().replace(/\s+/g, ' ');
      }

      _splitOffer(value) {
        const text = String(value || '').trim().replace(/\s+/g, ' ');
        const separator = text.lastIndexOf(' ');
        return separator < 0 ? { amount: text, text: '' } : { amount: text.slice(0, separator), text: text.slice(separator + 1) };
      }

      _updateTimer(expiry) {
        const remaining = expiry - Date.now();
        if (remaining <= 0) {
          window.clearInterval(this._timer);
          this._timer = 0;
          this.hidden = true;
          this._copyButton.disabled = true;
          return;
        }
        let seconds = Math.floor(remaining / 1000);
        const days = Math.floor(seconds / 86400);
        seconds %= 86400;
        const hours = Math.floor(seconds / 3600);
        seconds %= 3600;
        const minutes = Math.floor(seconds / 60);
        seconds %= 60;
        this.querySelector('[data-time-days]').textContent = String(days).padStart(2, '0');
        this.querySelector('[data-time-hours]').textContent = String(hours).padStart(2, '0');
        this.querySelector('[data-time-minutes]').textContent = String(minutes).padStart(2, '0');
        this.querySelector('[data-time-seconds]').textContent = String(seconds).padStart(2, '0');
      }

      async _copyCode() {
        const code = this._code?.textContent?.trim();
        if (!code || this._copyButton?.disabled) return;
        const revision = this._revision;
        try {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
          await navigator.clipboard.writeText(code);
          if (!this.isConnected || revision !== this._revision || code !== this._code.textContent.trim()) return;
          this._status.textContent = 'Coupon code copied.';
          this._copyButton.textContent = 'Copied';
          window.clearTimeout(this._copyReset);
          this._copyReset = window.setTimeout(() => {
            if (this.isConnected) this._copyButton.textContent = this._config.copyLabel || 'Copy';
          }, 1800);
        } catch {
          if (!this.isConnected || revision !== this._revision || code !== this._code.textContent.trim()) return;
          this._status.textContent = 'Copy failed. Select and copy the coupon code.';
          this._code.setAttribute('tabindex', '0');
          const range = document.createRange();
          range.selectNodeContents(this._code);
          const selection = window.getSelection();
          selection.removeAllRanges();
          selection.addRange(range);
          this._code.focus();
        }
      }
    });
  }

  const initialize = (root = document) => root.querySelectorAll?.('listing-coupon-card').forEach((element) => {
    if (!element._connected) element.connectedCallback();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  else initialize();
})();
